/*
 * Ryan O'Dowd
 * 2025-05-26
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import {
  useCallback,
  useEffect,
  useState,
} from 'react';

export default function useTimeRemaining(wrinkle, isPaused) {
  const _getSecondsUntilNow = useCallback((timestamp) => {
    if (!timestamp) {
      return 0;
    }

    return (new Date(timestamp).valueOf() - Date.now()) / 1000;
  }, []);

  const [_secondsRemaining, setSecondsRemaining] = useState(() => _getSecondsUntilNow(wrinkle?.date_next_srs_level));

  useEffect(() => {
    const updateTimeRemaining = () => {
      setSecondsRemaining(_getSecondsUntilNow(wrinkle?.date_next_srs_level));
    };

    updateTimeRemaining(); // Set initial time
    let timer;
    if (
      !isPaused &&
      wrinkle?.date_next_srs_level &&
      _getSecondsUntilNow(wrinkle.date_next_srs_level) >= 0 &&
      _getSecondsUntilNow(wrinkle.date_next_srs_level) < 86400
    ) {
      timer = setInterval(updateTimeRemaining, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isPaused, wrinkle?.date_next_srs_level, _getSecondsUntilNow]);

  const _prettyTimeDiff = (numSeconds) => {
    if (numSeconds <= 0) {
      return ''; // button text should never be null, but when we level up, the old wrinkle date_last_passed value will be displayed until the next render
    }

    const days = Math.floor(numSeconds / 86400);
    const hours = Math.floor((numSeconds % 86400) / 3600);
    const minutes = Math.floor((numSeconds % 3600) / 60);
    const seconds = Math.floor((numSeconds % 60) % 60);

    if (days) {
      return `level up in ${days} days`;
    }

    return `${hours || ''}${hours ? ':' : ''}${`${minutes}`.padStart(2, '0')}:${`${seconds}`.padStart(2, '0')}`;
  };

  return _prettyTimeDiff(_secondsRemaining);
}
